import { Head, Link } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { UserDisplay } from "@/components/user-display"
import { RoleBadge } from "@/components/role-badge"
import { StatusIndicator } from "@/components/status-indicator"
import { Pencil } from "lucide-react"
import type { UserSummary } from "@/types"

type Props = {
  user: UserSummary
}

function UserShow({ user }: Props) {
  return (
    <>
      <Head title={`${user.first_name} ${user.last_name}`} />
      <PageHeader title={`${user.first_name} ${user.last_name}`} back="/users" />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <UserDisplay user={user} size="lg" showEmail />
            <StatusIndicator active={user.is_active} />
            {user.is_super_admin && <Badge variant="destructive">Super Admin</Badge>}
          </div>
          <Button asChild>
            <Link href={`/users/${user.id}/edit`}>
              <Pencil className="size-4" />
              Modifier
            </Link>
          </Button>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Informations</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-muted-foreground text-sm">Rôle</p>
                <RoleBadge role={user.role} />
              </div>
              <div>
                <p className="text-muted-foreground text-sm">Organisation</p>
                <p>{user.organisation?.name || "—"}</p>
              </div>
              {user.job_title && (
                <div>
                  <p className="text-muted-foreground text-sm">Poste</p>
                  <p>{user.job_title}</p>
                </div>
              )}
              {user.phone_number && (
                <div>
                  <p className="text-muted-foreground text-sm">Téléphone</p>
                  <p>{user.phone_number}</p>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Équipes</CardTitle>
            </CardHeader>
            <CardContent>
              {user.teams && user.teams.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {user.teams.map((team) => (
                    <span key={team.id} className="rounded-md border px-3 py-1 text-sm">
                      {team.name}
                    </span>
                  ))}
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">Aucune équipe</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  )
}

UserShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default UserShow
